import {
  Box,
  FormControl,
  MenuItem,
  Select,
  SelectChangeEvent,
} from "@mui/material";
import ApartmentIcon from "@mui/icons-material/Apartment";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { ClientTypes } from "../../features/auth/types/authType";
import { selectAuth } from "../../features/auth/authSlice";
import {
  selectPreference,
  setForeignAgent,
} from "../../features/preferences/preferencesSlice";

const ALL = "all";

/**
 * Inline foreign-office (ForeignAgent) filter shown above list toolbars.
 * Replaces the office picker that used to live in the Filters drawer.
 * Drives the global (persisted) `agentId` preference; self-hides when the
 * user has no foreign offices.
 */
export const OfficeFilter = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { user } = useAppSelector(selectAuth);
  const { agentId } = useAppSelector(selectPreference);

  const offices = (user?.company?.partners ?? []).filter(
    (c) => c.type === ClientTypes.ForeignAgent,
  );

  if (offices.length === 0) return null;

  const value = offices.some((o) => String(o._id) === String(agentId))
    ? (agentId as string)
    : ALL;

  const handleChange = (e: SelectChangeEvent) => {
    const next = e.target.value;
    dispatch(setForeignAgent(next === ALL ? undefined : next));
  };

  const labelFor = (val: string) =>
    val === ALL
      ? t("allOffices")
      : offices
            .find((o) => String(o._id) === String(val))
            ?.displayName.includes(" ")
        ? offices
            .find((o) => String(o._id) === String(val))
            ?.displayName.split(" ")[0]
        : (offices.find((o) => String(o._id) === String(val))?.displayName ??
          t("allOffices"));

  return (
    <FormControl size="small" sx={{ minWidth: 170, flexShrink: 0 }}>
      <Select
        value={value}
        onChange={handleChange}
        renderValue={(val) => (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <ApartmentIcon fontSize="small" />
            <Box
              component="span"
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                fontWeight: 700,
              }}
            >
              {labelFor(val)}
            </Box>
          </Box>
        )}
        MenuProps={{ PaperProps: { sx: { maxHeight: 360 } } }}
        sx={{
          borderRadius: 999,
          fontWeight: 700,
          color: "secondary.main",
          backgroundColor: "background.paper",
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "secondary.main",
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "secondary.dark",
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "secondary.dark",
          },
          "& .MuiSvgIcon-root": { color: "secondary.main" },
          "& .MuiSelect-select": {
            py: 0.6,
            display: "flex",
            alignItems: "center",
          },
        }}
      >
        <MenuItem value={ALL}>{t("allOffices")}</MenuItem>
        {offices.map((o) => (
          <MenuItem key={o._id} value={o._id}>
            {o.displayName.includes(" ")
              ? o.displayName.split(" ")[0]
              : o.displayName}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};
